import { ErrorMessage, Field, Form, Formik, FormikHelpers } from 'formik';
import { useAtom } from 'jotai';
import React, { FC, useState } from 'react';
import { useTranslation } from 'react-i18next';
import * as yup from 'yup';

import { profileAtom } from '../../../jotai-atoms/profile.atom';
import { ProfileService } from '../../../services/profile';
import { InputFeedback } from '../../../stories/InputFeedback';
import { Modal } from '../../../stories/Modal';
import { Panel } from '../../../stories/Panel';
import { TextInput } from '../../../stories/TextInput';
import { useUpdatePassword } from '../../../hooks/use-update-password';

interface ChangePasswordModalProps {
  show: boolean;
  onClose: () => void;
}

export interface ChangePasswordParams {
  password1: string | null;
  password2: string | null;
  old_password: string | null;
}

const ChangePasswordModal: FC<ChangePasswordModalProps> = (props) => {
  const __ = useTranslation().t;
  const [errorStateActive, setErrorStateActive] = useState<boolean>(false);
  const [profile] = useAtom(profileAtom);
  const { mutateAsync, isLoading } = useUpdatePassword();
  const initialValues: ChangePasswordParams = {
    password1: null,
    password2: null,
    old_password: null,
  };

  const schema = yup.object().shape({
    password1: yup
      .string()
      .nullable()
      .required(__('fieldRequired'))
      .min(8, __('passwordRequirementsNotMet')),
    password2: yup
      .string()
      .nullable()
      .required(__('fieldRequired'))
      .min(8, __('passwordRequirementsNotMet')),
    old_password: yup.string().nullable().required(__('fieldRequired')),
  });

  const modalProps = {
    show: props.show,
    type: 'default',
    title: __('changePassword'),
    submitButtonText: __('save'),
    cancelButtonText: __('cancel'),
  };

  const onSubmit = (
    values: ChangePasswordParams,
    formikHelpers: FormikHelpers<ChangePasswordParams>,
  ) => {
    if (
      values.password1 !== values.password2 ||
      values.password1 === null ||
      values.password2 === null ||
      values.old_password === null
    ) {
      formikHelpers.setSubmitting(false);
      formikHelpers.setFieldError('password2', __('passwordMismatch'));
      return;
    }
    const changePasswordWithToken = {
      old_password: values.old_password,
      new_password: values.password1,
      new_password_confirmation: values.password2,
      _token: profile?._token
    }
    mutateAsync(changePasswordWithToken, {
      onSuccess: () => {
        setErrorStateActive(false);
        props.onClose();
      },
      onError: () => {
        setErrorStateActive(true);
      }
    });
  };

  const onClose = (resetForm: () => void) => {
    resetForm();
    setErrorStateActive(false);
    props.onClose();
  };

  return (
    <Formik initialValues={initialValues} validationSchema={schema} onSubmit={onSubmit}>
      {({ submitForm, resetForm }) => (
        <Modal
          {...modalProps}
          submitButtonDisabled={isLoading}
          onClose={() => onClose(resetForm)}
          onSubmit={submitForm}
        >
          <Form className="oejr-profile-modal-form">
            <Panel
              variant="danger"
              hidden={!errorStateActive}
              onHide={() => setErrorStateActive(false)}
            >
              <p>{__('invalidPassword')}</p>
            </Panel>
            <Field
              type="password"
              name="old_password"
              label={__('currentPassword')}
              component={TextInput}
            />
            <ErrorMessage
              name="old_password"
              component={({ children }: any) => (
                <InputFeedback state="error" text={children} icon="exclamation-circle" />
              )}
            />
            <Field
              type="password"
              name="password1"
              label={__('newPassword')}
              component={TextInput}
            />
            <ErrorMessage
              name="password1"
              component={({ children }: any) => (
                <InputFeedback state="error" text={children} icon="exclamation-circle" />
              )}
            />
            <Field
              type="password"
              name="password2"
              label={__('repeatPassword')}
              component={TextInput}
            />
            <ErrorMessage
              name="password2"
              component={({ children }: any) => (
                <InputFeedback state="error" text={children} icon="exclamation-circle" />
              )}
            />
          </Form>
        </Modal>
      )}
    </Formik>
  );
};

export default ChangePasswordModal;
